import { call, put, takeLatest, select, take, race } from 'redux-saga/effects'
import { delay } from 'redux-saga'
import * as actions from './actions'
import * as c from './constants'
import selectors from './selectors'
import BotService from './services/BotService'
import Raven from 'raven-js'
import {
  addUserMessage, addResponseMessage, addCarousel,
  setQuickButtons, setMenu, setInputDisabled, setInputDateType, setInputDropdownType, setDropdownOptions, setTheme,
  setIsFetching, setStyles, setSenderPlacholder, setToc, setBlink, setBlinkColor, clearInput
} from '../ChatWidget'
import { pushEvent } from 'hocs/TrackingHOC/actions'
import Pusher from 'pusher-js'
import { addMessageListener, getNotes } from 'helpers'
import { store } from 'src/store'
import { resetChat } from 'widget/store/actions'
import UserFriendlyError from 'helpers/UserFriendlyError'
const diff = require('diff')

const POLL_FREQUENCY = 500

// Store last module details to include in analytics payload
let lastModuleId = null
let lastModuleTitle = null
let conversationId = null

function* onStartConversationSaga(action) {
  const { startingModule, skipHistory } = action.payload || {}

  let initialized = yield select(selectors.initialized)
  if (!initialized) {
    setIsFetching(true)
    yield call(BotService.init)
    conversationId = yield call(BotService.getConversationId)
    // Skip history if customer optin to skip it in the customer options
    const historyPresent = skipHistory ? false : yield call(loadHistory)
    sessionStorage.setItem('notes_before_session', getNotes())
    if (historyPresent) {
      yield put(actions.botInit())
      yield put(actions.fetchBotResponseSuccess())
    } else {
      yield put(actions.newUserMessage({
        text: ' ',
        module: startingModule
      }))
    }

  }
}

function* loadHistory() {
  const history = yield call(BotService.getHistory)
  yield put(resetChat())
  history.forEach(item => {
    if (item.userMessage) {
      return addUserMessage(item.userMessage, item.timestamp)
    }
    return addBotMessage(item.botMessage, item.timestamp)
  })

  // After full-refresh we lose lastModule details and need to
  // load it from history
  const lastResponse = history.reverse().find(h => h.botMessage)
  if (lastResponse) {
    lastModuleTitle = lastResponse.botMessage.moduleTitle
    lastModuleId = lastResponse.botMessage.module
  }

  return history.length > 0
}

function addBotMessage(response, timestamp) {
  let {
    messages, quickReplies, menu, carousel, forceReply, dateInput, blink, blinkColor, dropdownInput, dropdownOptions,
    theme, styles, senderPlaceHolder, toc
  } = response

  if (theme) { setTheme(theme) }
  if (menu) { setMenu(menu.toJSON()) }
  if (toc) { setToc(toc.toJSON()) }
  if (styles) { setStyles(styles) }
  setSenderPlacholder(senderPlaceHolder)
  messages.forEach(msg => addResponseMessage(msg.text, timestamp))
  setQuickButtons(quickReplies)
  if (carousel) { addCarousel(carousel) }
  setInputDisabled(forceReply)
  if (blink) {setBlink(blink)}
  if (blinkColor) {setBlinkColor(blinkColor)}
  if (dropdownInput) {
    setInputDropdownType(dropdownInput)
    setDropdownOptions(dropdownOptions)
  } else {
    setInputDropdownType(false)
    setDropdownOptions([])
  }
  if (dateInput) { setInputDateType(dateInput) }

}

function onCloseConversationSaga() {
  BotService.reset()
}

function clearInputs() {
  setQuickButtons([])
  setInputDisabled(false)
  setBlink(false)
  setBlinkColor('')
  setInputDropdownType(false)
  setDropdownOptions([])
  setInputDateType(false)
}

function* newUserMessageSaga({ action, msg, module, buttonLabel }) {
  clearInputs()
  setIsFetching(true)

  const response = yield call(fetchResponse, {
    buttonLabel,
    text: msg,
    startingModule: module
  })

  yield call(handleBotResponse, response, action)
}

function* menuClickSaga({ action, msg, buttonLabel }) {
  clearInputs()
  setIsFetching(true)

  const response = yield call(fetchResponse, {
    buttonLabel,
    text: msg,
    global: true
  })

  yield call(handleBotResponse, response, action)
}

function* handleBotResponse(response, prevAction) {
  if (!response) { return null }
  let {
    messages, quickReplies, menu, carousel, forceReply, dateInput, blink, blinkColor, dropdownInput, dropdownOptions,
    fetchNext, theme, styles, senderPlaceHolder, waitFor, dbNav, dbNavTitle, toc
  } = response

  let nextModule
  if (waitFor) {
    try {
      const pusherResponse = yield call(waitForPusher, waitFor)
      // if pusher supplies a message show it on the screen, otherwise just move to the next module
      if(!!pusherResponse // if the response is 'truthy' (not null or undefined or false or 0 or '')
        && typeof pusherResponse === 'object'
        && !Array.isArray(pusherResponse) // typeof [] === 'object'
        && typeof pusherResponse.message === 'string'
        && pusherResponse.message !== ''){
        messages = [{ text: pusherResponse.message }]
      }
      else {
        messages = []
      }

      if(pusherResponse.nextModule){
        fetchNext = true
        nextModule = pusherResponse.nextModule
      }

    } catch(e) {
      Raven.captureException(e)
      console.log('Pusher error', e)
    }
  }

  setIsFetching(false)

  if (response.error) {
    yield call(handleBotError, response.error)
    return null
  }

  if (theme) { setTheme(theme) }
  if (menu) { setMenu(menu.toJSON()) }
  if (toc) { setToc(toc.toJSON()) }
  if (styles) { setStyles(styles) }
  setSenderPlacholder(senderPlaceHolder)
  yield put(actions.botInit())

  for (let i = 0; i < messages.length; i++) {
    let msg = messages[i]
    if (msg.delay > 0) {
      setIsFetching(true)
      if (process.env.NODE_ENV !== 'development') {
        yield delay(msg.delay)
      }
      setIsFetching(false)
    }

    yield call(addResponseMessage, msg.text, Date.now())
  }

  setQuickButtons(quickReplies)
  // TODO: make one function for setInput
  if (carousel) { addCarousel(carousel) }
  if (forceReply) { setInputDisabled(forceReply) }
  if (blink) {setBlink(blink)}
  if (blinkColor) {setBlinkColor(blinkColor)}
  if (dropdownInput) {
    setInputDropdownType(dropdownInput)
    setDropdownOptions(dropdownOptions)
  }
  if (dateInput) { setInputDateType(dateInput) }
  if (dbNav) { triggerEvent('dbNav', dbNav, dbNavTitle) }

  yield put(actions.fetchBotResponseSuccess())

  if (fetchNext) {
    setIsFetching(true)
    let text = prevAction.payload.value || prevAction.payload.text || ' '
    let newResp = yield call(fetchResponse, { text, startingModule: nextModule })
    yield call(handleBotResponse, newResp, prevAction)
  }
}

function handleBotError(err) {
  const defaultErrorMsg = "I'm sorry, there was an issue getting the response."
    + " Please start over."
  setIsFetching(false)
  console.error(err)

  if (err instanceof UserFriendlyError) {
    return addResponseMessage(err.message, Date.now())
  }

  const translatedError = BotService.translateErrorMessage(err.message)
  if (translatedError) {
    return addResponseMessage(translatedError, Date.now())
  }

  return addResponseMessage(defaultErrorMsg, Date.now())
}

function* fetchResponse({ text, global, startingModule }) {
  try {
    const response = yield call(BotService.getBotResponse, text, global, startingModule)

    if (response.poll) {
      yield put(actions.startPolling())
      setIsFetching(false)
      return
    }
    yield put(actions.stopPolling())
    lastModuleId = response.module
    lastModuleTitle = response.moduleTitle
    conversationId = response.conversationId

    const msgString = response.messages.map(msg => msg.text).join('')

    trackAction('bot', msgString)

    yield put(pushEvent('botInteraction', {
      direction: 'out',
      reply: msgString,
      module: lastModuleId,
      // Send conversationId as session_id to avoid changes
      // to current reports implemenation
      session_id: conversationId,
      module_title: lastModuleTitle
    }))

    return response

  } catch(error) {
    Raven.captureException(error)
    return { error }
  }
}

function* handleClick(fallback, action) {
  const isLive = yield select(selectors.isLive)
  const { type, text, value, name, module } = action.payload
  if (type === 'url') {
    return window.open(value, '_blank')
  }

  if (type === 'event') {
    return triggerEvent(name, value)
  }

  const msg = value || text

  trackAction('user', msg)

  // Handle live tracking in adapter
  if (!isLive) {
    yield put(pushEvent('botInteraction', {
      buttonLabel: text,
      direction: 'in',
      reply: msg,
      module: lastModuleId,
      // Send conversationId as session_id to avoid changes
      // to current reports implemenation
      session_id: conversationId,
      module_title: lastModuleTitle
    }))
  }


  if (msg.toLowerCase() === 'restart') {
    sessionStorage.setItem('notes_before_session', getNotes())
    clearInput(true)
  } 
  addUserMessage(msg, Date.now())
  yield call(fallback, { action, msg, module, buttonLabel: text })
  clearInput(false)
}

function triggerEvent(type, value, title = null) {
  eventTarget().postMessage({ type, value, title }, '*')
}

function eventTarget() {
  if (window.opener && !window.opener.closed) {
    return window.opener
  }

  return window
}

function waitForPusher(event) {
  return new Promise(resolve => {
    const pusher = new Pusher(process.env.REACT_APP_PUSHER_API_KEY, {
      authEndpoint: process.env.REACT_APP_PUSHER_AUTH_ENDPOINT,
      cluster: 'mt1',
      forceTLS: true
    })

    const channel = pusher.subscribe(`private-${conversationId}`)
    if(event === 'addReact-listener') {
      channel.bind('pusher:subscription_succeeded', function(data) {
        return channel.trigger('client-trigger-3C-send', {event})
      })
    }

    channel.bind(event, function(data) {
      resolve(data)
    })
  })
}

function trackAction(sender, msg) {
  if (msg.trim() === '') { return }

  const sessionId = localStorage.getItem('sessionId')
  const timestamp = new Date().toISOString()

  let allSessions = sessionStorage.getItem('bot_sessions')
  allSessions = (allSessions && JSON.parse(allSessions)) || []

  let currentSession = allSessions.find(item => item.session === sessionId)

  if (!currentSession) {
    currentSession = { session: sessionId, timestamp, actions: [], notes: [] }
  }

  currentSession.actions = [
    ...currentSession.actions, { sender, msg, timestamp }
  ]

  currentSession.notes = getNotesDiff()

  allSessions = [
    currentSession,
    ...allSessions.filter(item => item.session !== sessionId)
  ]

  sessionStorage.setItem('bot_sessions', JSON.stringify(allSessions))
}

function getNotesDiff() {
  const oldNotes = sessionStorage.getItem('notes_before_session')
  const newNotes = getNotes()
  if (!oldNotes || !newNotes) { return [] }
  const dff = diff.diffLines(oldNotes, newNotes, { newlineIsToken: false })
  return dff.filter(item => item.added === true).map(item => item.value)
}


function* pollWorker() {
  while (true) {
    try {
      const response = yield call(BotService.poll)
      response.forEach(msg => addResponseMessage(msg.response, msg.timestamp, true))

      yield call(delay, POLL_FREQUENCY)
    } catch (e) {
      handleBotError(e)
      yield put(actions.stopPolling())
    }
  }
}


export default function* watcher() {
  yield takeLatest(c.START_CONVERSATION, onStartConversationSaga)
  yield takeLatest(c.CLOSE_CONVERSATION, onCloseConversationSaga)
  yield takeLatest(c.NEW_USER_MSG, handleClick, newUserMessageSaga)
  yield takeLatest(c.DROPDOWN_ITEM_CLICK, handleClick, newUserMessageSaga)
  yield takeLatest(c.MENU_ITEM_CLICK, handleClick, menuClickSaga)
  yield takeLatest(c.LOAD_HISTORY, loadHistory)

  addMessageListener('dbOpenBot', data => {
    store.dispatch(actions.startConversation({
      startingModule: data.module
    }))
  })

  addMessageListener('devicebits:resetBot', () => {
    const initialized = selectors.initialized(store.getState())
    // No need to reset if bot was not initialized
    if (!initialized) { return }
    store.dispatch(actions.newUserMessage({ text: 'restart' }))
  })

  addMessageListener('dbSendBotMessage', data => {
    store.dispatch(actions.newUserMessage({ text: data.message }))
  })

  while (true) {
    yield take(c.START_POLLING)
    yield race([call(pollWorker), take(c.STOP_POLLING)])
  }

}



// WEBPACK FOOTER //
// ./src/App/sagas.js